/**
 * MonthlyReportTab (Admin) — same UI as the brand tab but hits the
 * /admin/access-module/monthly-report/:userId endpoints.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Input,
  InputNumber,
  Modal,
  Popover,
  Row,
  Select,
  Spin,
  Table,
  Tag,
  Timeline,
  Upload,
  message,
} from "antd";
import { DownloadOutlined, EditOutlined, ExclamationCircleOutlined, HistoryOutlined, MailOutlined, MinusOutlined, PlusOutlined, ReloadOutlined, SaveOutlined, UploadOutlined, SyncOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import lang from "../../helper/langHelper";

// Short month names used by the custom month picker grid
const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/**
 * MonthPicker — Popover-based 3×4 calendar grid.
 *
 * The antd DatePicker picker="month" rendered in 2 columns inside Tabs
 * (an unresolvable Ant Design v4 layout bug). This custom component uses a
 * Popover anchored to document.body so it always renders at full size and
 * shows all 12 months in the proper 3-column grid.
 */
const MonthPicker = ({ value, onChange }) => {
  const [open, setOpen] = React.useState(false);
  const [panelYear, setPanelYear] = React.useState(() => value ? value.year() : dayjs().year());

  const handleOpen = (v) => {
    setOpen(v);
    if (v && value) setPanelYear(value.year()); // always show the selected year when opening
  };

  const gridContent = (
    <div style={{ width: 236, userSelect: "none", padding: "4px 0" }}>
      {/* ── Year navigation row ─────────────────────────────────────── */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 10, padding: "0 2px",
      }}>
        <button
          onClick={(e) => { e.stopPropagation(); setPanelYear((y) => y - 1); }}
          style={{ border: "none", background: "none", cursor: "pointer", fontSize: 18, lineHeight: 1, color: "#1a3166", padding: "2px 8px" }}
        >‹</button>
        <span style={{ fontWeight: 700, fontSize: 15, color: "#1a1f3a" }}>{panelYear}</span>
        <button
          onClick={(e) => { e.stopPropagation(); setPanelYear((y) => y + 1); }}
          style={{ border: "none", background: "none", cursor: "pointer", fontSize: 18, lineHeight: 1, color: "#1a3166", padding: "2px 8px" }}
        >›</button>
      </div>

      {/* ── 3×4 month grid ──────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4 }}>
        {MONTHS_SHORT.map((m, i) => {
          const isSelected = value && value.year() === panelYear && value.month() === i;
          const isCurrentMonth = dayjs().year() === panelYear && dayjs().month() === i;
          return (
            <div
              key={m}
              role="button"
              tabIndex={0}
              onClick={() => {
                onChange(dayjs(`${panelYear}-${String(i + 1).padStart(2, "0")}-01`).startOf("month"));
                setOpen(false);
              }}
              onKeyDown={(e) => e.key === "Enter" && e.currentTarget.click()}
              style={{
                padding: "8px 4px",
                textAlign: "center",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: isSelected ? 700 : 400,
                background: isSelected
                  ? "#1a3166"
                  : isCurrentMonth
                  ? "#e8eef7"
                  : "transparent",
                color: isSelected ? "#ffffff" : isCurrentMonth ? "#1a3166" : "#374151",
                border: isSelected
                  ? "1px solid #1a3166"
                  : isCurrentMonth
                  ? "1px solid #c7d8f0"
                  : "1px solid transparent",
                transition: "background 0.15s, color 0.15s",
              }}
              onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "#f0f4ff"; }}
              onMouseLeave={(e) => {
                if (!isSelected) e.currentTarget.style.background = isCurrentMonth ? "#e8eef7" : "transparent";
              }}
            >
              {m}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <Popover
      content={gridContent}
      trigger="click"
      visible={open}
      onVisibleChange={handleOpen}
      getPopupContainer={() => document.body}
      overlayStyle={{ zIndex: 9999 }}
      overlayInnerStyle={{ padding: "10px 12px", borderRadius: 8 }}
    >
      <Button
        style={{
          minWidth: 160, textAlign: "left", borderRadius: 6,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}
      >
        <span>{value ? value.format("MMMM YYYY") : "Select month"}</span>
        <span style={{ color: "#999", fontSize: 10, marginLeft: 8 }}>▾</span>
      </Button>
    </Popover>
  );
};

const { TextArea } = Input;

const formatBlogStatusDisplay = (value = "") =>
  String(value).trim().toLowerCase() === "draft" ? "Draft" : value;

const formatReportDate = (value) => {
  if (!value) return "-";
  const raw = String(value).trim();
  const slashDate = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashDate) {
    return `${slashDate[1].padStart(2, "0")}/${slashDate[2].padStart(2, "0")}/${slashDate[3]}`;
  }
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : raw;
};

// Classifies an audit-trail change entry from its label text so it can be
// styled/iconed as an addition, removal, or a plain field update.
const getChangeKind = (label = "") => {
  if (/added/i.test(label)) return "add";
  if (/removed/i.test(label)) return "remove";
  return "update";
};
const CHANGE_KIND_STYLE = {
  add:    { color: "#389e0d", bg: "#f0fbf0", border: "#b7eb8f" },
  remove: { color: "#cf1322", bg: "#fff1f0", border: "#ffa39e" },
  update: { color: "#1a3166", bg: "#f6f8fa", border: "#dbe4f3" },
};

// Manual override for an auto-computed KPI (current + prev month). null =
// "not overridden, use the computed value shown above" — the placeholder
// shows that computed value so it's clear what leaving it blank means.
// Hoisted to module scope, not defined inside MonthlyReportTab's render body
// — a component defined inline in a render function is a NEW component type
// on every render, so React remounts its InputNumbers on every keystroke
// instead of just re-rendering them, dropping focus after each digit typed.
const OverrideInputs = ({ value, onChange, prevValue, onPrevChange, autoValue, autoPrev }) => (
  <Row gutter={8} style={{ marginTop: 8 }}>
    <Col span={12}>
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 2 }}>This Month <span style={{ color: "#f5222d" }}>*</span></div>
      <InputNumber
        value={value}
        onChange={onChange}
        min={0}
        placeholder={autoValue != null ? String(autoValue) : "auto"}
        style={{ width: "100%" }}
        size="small"
      />
    </Col>
    <Col span={12}>
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 2 }}>Previous Month <span style={{ color: "#f5222d" }}>*</span></div>
      <InputNumber
        value={prevValue}
        onChange={onPrevChange}
        min={0}
        placeholder={autoPrev != null ? String(autoPrev) : "auto"}
        style={{ width: "100%" }}
        size="small"
      />
    </Col>
  </Row>
);

const MonthlyReportTab = () => {
  const { id } = useParams();
  const { request } = useRequest();
  // useRequest returns a NEW `request` function on every render — we stash it
  // in a ref so memoized callbacks aren't invalidated each cycle. Without this
  // the tab would infinite-loop and never finish loading.
  const requestRef = useRef(request);
  requestRef.current = request;

  const [selectedMonth, setSelectedMonth] = useState(dayjs().startOf("month"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // ── Send Mail modal state ────────────────────────────────────────────────
  const [mailModalOpen, setMailModalOpen] = useState(false);
  const [mailSubject, setMailSubject] = useState("");
  const [mailBody, setMailBody] = useState("");
  // To/Cc are typed by hand — pre-filled with the client's own address as a
  // convenience starting point (To only), but fully editable/removable.
  const [mailTo, setMailTo] = useState([]);
  const [mailCc, setMailCc] = useState([]);
  const [sendingMail, setSendingMail] = useState(false);

  // ── Audit trail state ────────────────────────────────────────────────────
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [recomputingStyles, setRecomputingStyles] = useState(false);
  const [needsContentStyleRecompute, setNeedsContentStyleRecompute] = useState(false);

  const [executiveSummary, setExecutiveSummary] = useState("");
  const [themesFocused, setThemesFocused] = useState("");
  const [goalsNextMonth, setGoalsNextMonth] = useState("");
  const [ssiScore, setSsiScore] = useState(null);
  const [ssiScorePrev, setSsiScorePrev] = useState(null);
  const [profileViews, setProfileViews] = useState(null);
  const [profileViewsPrev, setProfileViewsPrev] = useState(null);
  const [blogsStatus, setBlogsStatus] = useState([]);
  // KPI overrides — null means "not overridden, use the computed value".
  const [postsCreatedOverride, setPostsCreatedOverride] = useState(null);
  const [postsCreatedPrevOverride, setPostsCreatedPrevOverride] = useState(null);
  const [postsPublishedOverride, setPostsPublishedOverride] = useState(null);
  const [postsPublishedPrevOverride, setPostsPublishedPrevOverride] = useState(null);
  const [followersOverride, setFollowersOverride] = useState(null);
  const [followersPrevOverride, setFollowersPrevOverride] = useState(null);
  const [contentImpressionsOverride, setContentImpressionsOverride] = useState(null);
  const [contentImpressionsPrevOverride, setContentImpressionsPrevOverride] = useState(null);
  const [contentEngagementOverride, setContentEngagementOverride] = useState(null);
  const [contentEngagementPrevOverride, setContentEngagementPrevOverride] = useState(null);
  // Engagement tracking table — seeded from the live computed rows until the
  // admin edits/adds a row, then persisted as-is (same pattern as blogsStatus).
  const [engagementRows, setEngagementRows] = useState([]);
  // Posts removed from the Engagement Tracking table (their rows come from real posts).
  const [excludedEngagementPostIds, setExcludedEngagementPostIds] = useState([]);
  const [topPostImage, setTopPostImage] = useState("");
  const [topPostUrlOverride, setTopPostUrlOverride] = useState("");
  // Full manual override of the Top Performing Post card — used when there's
  // no app-published post to auto-pick (only manually-added Engagement
  // Tracking rows this month) or to hand-correct the auto-picked numbers.
  const [topPostTitleOverride, setTopPostTitleOverride] = useState("");
  const [topPostLikesOverride, setTopPostLikesOverride] = useState(null);
  const [topPostCommentsOverride, setTopPostCommentsOverride] = useState(null);
  const [topPostSharesOverride, setTopPostSharesOverride] = useState(null);

  // ── Themes checkboxes ────────────────────────────────────────────────────
  // `themes`        — full list of ContentTheme docs from DB for this client
  // `selectedThemes`— map of { [themeId]: string[] of selected subPillars }
  const [themes, setThemes] = useState([]);
  const [selectedThemes, setSelectedThemes] = useState({});

  // Serialize selectedThemes → JSON string stored in themesFocused
  const buildThemesJson = (currentThemes, currentSelected) => {
    const result = currentThemes
      .filter((t) => (currentSelected[String(t._id)] || []).length > 0)
      .map((t) => ({
        themeId: String(t._id),
        name: t.name,
        subPillars: currentSelected[String(t._id)] || [],
      }));
    return result.length ? JSON.stringify(result) : "";
  };

  const handleToggleSubpillar = (themeId, sub) => {
    setSelectedThemes((prev) => {
      const curr = prev[themeId] || [];
      const next = curr.includes(sub) ? curr.filter((s) => s !== sub) : [...curr, sub];
      const updated = { ...prev, [themeId]: next };
      setThemesFocused(buildThemesJson(themes, updated));
      return updated;
    });
  };

  const handleToggleTheme = (themeId, allSubs) => {
    setSelectedThemes((prev) => {
      const curr = prev[themeId] || [];
      const allChecked = allSubs.length > 0 && allSubs.every((s) => curr.includes(s));
      const next = allChecked ? [] : [...allSubs];
      const updated = { ...prev, [themeId]: next };
      setThemesFocused(buildThemesJson(themes, updated));
      return updated;
    });
  };

  const year = selectedMonth.year();
  const month = selectedMonth.month() + 1;

  const fetchReport = useCallback(() => {
    setLoading(true);
    requestRef.current({
      url: `/admin/access-module/monthly-report/${id}?year=${year}&month=${month}`,
      method: "GET",
      onSuccess: (res) => {
        setLoading(false);
        const payload = res?.data || null;
        setData(payload);
        if (payload?.editable) {
          const e = payload.editable;
          setExecutiveSummary(e.executiveSummary || "");
          setThemesFocused(e.themesFocused || "");
          setGoalsNextMonth(e.goalsNextMonth || "");
          setSsiScore(e.ssiScore);
          setSsiScorePrev(e.ssiScorePrev);
          setProfileViews(e.profileViews);
          setProfileViewsPrev(e.profileViewsPrev);
          setBlogsStatus(Array.isArray(e.blogsStatus) ? e.blogsStatus : []);
          setPostsCreatedOverride(e.postsCreatedOverride ?? null);
          setPostsCreatedPrevOverride(e.postsCreatedPrevOverride ?? null);
          setPostsPublishedOverride(e.postsPublishedOverride ?? null);
          setPostsPublishedPrevOverride(e.postsPublishedPrevOverride ?? null);
          setFollowersOverride(e.followersOverride ?? null);
          setFollowersPrevOverride(e.followersPrevOverride ?? null);
          setContentImpressionsOverride(e.contentImpressionsOverride ?? null);
          setContentImpressionsPrevOverride(e.contentImpressionsPrevOverride ?? null);
          setContentEngagementOverride(e.contentEngagementOverride ?? null);
          setContentEngagementPrevOverride(e.contentEngagementPrevOverride ?? null);
          setEngagementRows(Array.isArray(e.engagementRows) ? e.engagementRows : []);
          setExcludedEngagementPostIds(Array.isArray(e.excludedEngagementPostIds) ? e.excludedEngagementPostIds : []);
          setTopPostImage(e.topPostImage || "");
          setTopPostUrlOverride(e.topPostUrlOverride || "");
          setTopPostTitleOverride(e.topPostTitleOverride || "");
          setTopPostLikesOverride(e.topPostLikesOverride ?? null);
          setTopPostCommentsOverride(e.topPostCommentsOverride ?? null);
          setTopPostSharesOverride(e.topPostSharesOverride ?? null);
          setNeedsContentStyleRecompute(!!e.needsContentStyleRecompute);
          // ── Restore checkbox state from saved JSON ────────────────────
          try {
            const stored = e.themesFocused || "";
            if (stored) {
              const arr = JSON.parse(stored);
              if (Array.isArray(arr)) {
                const map = {};
                arr.forEach((item) => {
                  if (item.themeId) map[item.themeId] = Array.isArray(item.subPillars) ? item.subPillars : [];
                });
                setSelectedThemes(map);
              }
            } else {
              setSelectedThemes({});
            }
          } catch {
            setSelectedThemes({});
          }
        }
      },
      onError: () => {
        setLoading(false);
        message.error("Failed to load monthly report.");
      },
    });
    // requestRef is a ref — intentionally NOT in deps, otherwise infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, year, month]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // ── Force-refresh (bust Redis cache, fetch live from LinkedIn) ──────────────
  const handleRefresh = () => {
    setRefreshing(true);
    requestRef.current({
      url: `/admin/access-module/monthly-report/${id}?year=${year}&month=${month}&force=true`,
      method: "GET",
      onSuccess: (res) => {
        setRefreshing(false);
        const payload = res?.data || null;
        setData(payload);
        if (payload?.editable) {
          const e = payload.editable;
          setExecutiveSummary(e.executiveSummary || "");
          setThemesFocused(e.themesFocused || "");
          setGoalsNextMonth(e.goalsNextMonth || "");
          setSsiScore(e.ssiScore);
          setSsiScorePrev(e.ssiScorePrev);
          setProfileViews(e.profileViews);
          setProfileViewsPrev(e.profileViewsPrev);
          setBlogsStatus(Array.isArray(e.blogsStatus) ? e.blogsStatus : []);
          setPostsCreatedOverride(e.postsCreatedOverride ?? null);
          setPostsCreatedPrevOverride(e.postsCreatedPrevOverride ?? null);
          setPostsPublishedOverride(e.postsPublishedOverride ?? null);
          setPostsPublishedPrevOverride(e.postsPublishedPrevOverride ?? null);
          setFollowersOverride(e.followersOverride ?? null);
          setFollowersPrevOverride(e.followersPrevOverride ?? null);
          setContentImpressionsOverride(e.contentImpressionsOverride ?? null);
          setContentImpressionsPrevOverride(e.contentImpressionsPrevOverride ?? null);
          setContentEngagementOverride(e.contentEngagementOverride ?? null);
          setContentEngagementPrevOverride(e.contentEngagementPrevOverride ?? null);
          setEngagementRows(Array.isArray(e.engagementRows) ? e.engagementRows : []);
          setExcludedEngagementPostIds(Array.isArray(e.excludedEngagementPostIds) ? e.excludedEngagementPostIds : []);
          setTopPostImage(e.topPostImage || "");
          setTopPostUrlOverride(e.topPostUrlOverride || "");
          setTopPostTitleOverride(e.topPostTitleOverride || "");
          setTopPostLikesOverride(e.topPostLikesOverride ?? null);
          setTopPostCommentsOverride(e.topPostCommentsOverride ?? null);
          setTopPostSharesOverride(e.topPostSharesOverride ?? null);
          setNeedsContentStyleRecompute(!!e.needsContentStyleRecompute);
        }
        message.success("Report refreshed with live data.");
      },
      onError: () => {
        setRefreshing(false);
        message.error("Failed to refresh report.");
      },
    });
  };

  // ── Fetch client's content themes (for checkboxes) ────────────────────────
  const fetchThemes = useCallback(() => {
    requestRef.current({
      url: `/admin/access-module/content/${id}?limit=100&page=1`,
      method: "GET",
      onSuccess: (res) => {
        setThemes(res?.data?.themes || []);
      },
      onError: () => {}, // non-fatal — checkboxes just stay empty
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    fetchThemes();
  }, [fetchThemes]);

  const doSave = () => {
    setSaving(true);
    requestRef.current({
      url: `/admin/access-module/monthly-report/${id}?year=${year}&month=${month}`,
      method: "PUT",
      data: {
        executiveSummary,
        themesFocused,
        goalsNextMonth,
        ssiScore,
        ssiScorePrev,
        profileViews,
        profileViewsPrev,
        blogsStatus,
        engagementRows,
        excludedEngagementPostIds,
        postsCreatedOverride,
        postsCreatedPrevOverride,
        postsPublishedOverride,
        postsPublishedPrevOverride,
        followersOverride,
        followersPrevOverride,
        contentImpressionsOverride,
        contentImpressionsPrevOverride,
        contentEngagementOverride,
        contentEngagementPrevOverride,
        topPostImage,
        topPostUrlOverride,
        topPostTitleOverride,
        topPostLikesOverride,
        topPostCommentsOverride,
        topPostSharesOverride,
      },
      onSuccess: (res) => {
        setSaving(false);
        message.success("Monthly report saved.");
        if (res?.data) setData(res.data);
      },
      onError: () => {
        setSaving(false);
        message.error("Failed to save report.");
      },
    });
  };

  // Confirm before persisting — saving locks in the current numbers as
  // overrides, so live LinkedIn data no longer auto-refreshes those fields
  // until the override is cleared.
  const handleSave = () => {
    Modal.confirm({
      title: "Save this report?",
      icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
      content: (
        <div>
          Are you sure you want to save this report?
          <div style={{ marginTop: 8, color: "#b45309", fontSize: 13 }}>
            Once saved, the edited fields become fixed overrides — the live
            LinkedIn feed will no longer automatically refresh those values
            until the override is cleared.
          </div>
        </div>
      ),
      okText: "Save",
      cancelText: "Cancel",
      onOk: doSave,
    });
  };

  // Auto-save current state before download so uploaded image + edits are in the PDF
  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);

      await new Promise((resolve, reject) => {
        requestRef.current({
          url: `/admin/access-module/monthly-report/${id}?year=${year}&month=${month}`,
          method: "PUT",
          data: {
            executiveSummary,
            themesFocused,
            goalsNextMonth,
            ssiScore,
            ssiScorePrev,
            profileViews,
            profileViewsPrev,
            blogsStatus,
            engagementRows,
            excludedEngagementPostIds,
            postsCreatedOverride,
            postsCreatedPrevOverride,
            postsPublishedOverride,
            postsPublishedPrevOverride,
            followersOverride,
            followersPrevOverride,
            contentImpressionsOverride,
            contentImpressionsPrevOverride,
            contentEngagementOverride,
            contentEngagementPrevOverride,
            topPostImage,
            topPostUrlOverride,
            topPostTitleOverride,
            topPostLikesOverride,
            topPostCommentsOverride,
            topPostSharesOverride,
          },
          onSuccess: (res) => {
            if (res?.data) setData(res.data);
            resolve();
          },
          onError: (err) => reject(err),
        });
      });

      const token = localStorage.getItem("admin_token") || "";
      const url = `${apiPath.baseURL}/admin/access-module/monthly-report/${id}/pdf?year=${year}&month=${month}`;
      const response = await axios.get(url, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `Monthly_Report_${data?.leaderName || "report"}_${selectedMonth.format("MMM_YYYY")}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      message.success("PDF downloaded.");
    } catch (err) {
      message.error("Failed to download PDF.");
    } finally {
      setDownloading(false);
    }
  };

  // ── Send Mail ────────────────────────────────────────────────────────────
  // Builds a fresh, friendly default body every time the modal opens so it's
  // always in sync with the currently selected client/month, while still
  // letting the admin freely rewrite it before sending.
  const buildDefaultMailBody = () => {
    const firstName = (data?.leaderName || "there").split(" ")[0];
    const monthLabel = selectedMonth.format("MMMM YYYY");
    return `Hi ${firstName},

Hope you're doing well! Your Personal Branding activity report for ${monthLabel} is ready, and it's been a great month of building your presence on LinkedIn.

We've put together the highlights — content published, reach, engagement, and the top-performing post — in the attached PDF for a full breakdown.

A few things worth calling out this month:
• Consistent posting cadence across your key themes
• Strong engagement on your top post
• Steady growth in profile visibility

Take a look and let us know if you'd like to discuss the numbers or plan focus areas for next month. Always happy to jump on a quick call.

Talk soon!`;
  };

  const handleOpenMailModal = () => {
    setMailSubject(`Your ${selectedMonth.format("MMMM YYYY")} Monthly Activity Report`);
    setMailBody(buildDefaultMailBody());
    // Pre-fill To with the client's own email as a starting point — still
    // fully editable/removable; Cc always starts empty.
    setMailTo(data?.email ? [data.email] : []);
    setMailCc([]);
    setMailModalOpen(true);
  };

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // antd Select mode="tags" happily keeps whatever the user typed even if it
  // doesn't look like an email (e.g. a stray comma or half-typed address) —
  // filter those out before validating/sending so a typo doesn't silently
  // vanish the recipient or get sent as-is.
  const validateEmailTags = (list) => (Array.isArray(list) ? list : []).filter((v) => EMAIL_RE.test(String(v || "").trim()));

  const handleSendMail = () => {
    if (!mailBody.trim()) {
      message.error("Email body can't be empty.");
      return;
    }
    const toEmails = validateEmailTags(mailTo);
    if (!toEmails.length) {
      message.error("Enter at least one valid \"To\" email address.");
      return;
    }
    const ccEmails = validateEmailTags(mailCc);
    setSendingMail(true);
    requestRef.current({
      url: `/admin/access-module/monthly-report/${id}/send-mail?year=${year}&month=${month}`,
      method: "POST",
      data: { subject: mailSubject, bodyText: mailBody, to: toEmails, cc: ccEmails },
      onSuccess: (res) => {
        setSendingMail(false);
        setMailModalOpen(false);
        message.success(res?.message || "Report emailed.");
      },
      onError: (err) => {
        setSendingMail(false);
        message.error(err?.message || "Failed to send email.");
      },
    });
  };

  // ── Audit trail ──────────────────────────────────────────────────────────
  const handleOpenAuditModal = () => {
    setAuditModalOpen(true);
    setAuditLoading(true);
    requestRef.current({
      url: `/admin/access-module/monthly-report/${id}/audit-log?year=${year}&month=${month}`,
      method: "GET",
      onSuccess: (res) => {
        setAuditLoading(false);
        setAuditLogs(res?.data?.logs || []);
      },
      onError: () => {
        setAuditLoading(false);
        message.error("Failed to load audit trail.");
      },
    });
  };

  // ── Recompute Content Style ──────────────────────────────────────────────
  // One-off catch-up for reports saved before real post-type detection
  // (Image/Video/Poll/Carousel) existed — re-derives Content Style for every
  // saved row that's still tied to an app-published post, without touching
  // manual rows or any other edited field. Logged to the audit trail.
  const doRecomputeContentStyles = () => {
    setRecomputingStyles(true);
    requestRef.current({
      url: `/admin/access-module/monthly-report/${id}/recompute-content-style?year=${year}&month=${month}`,
      method: "POST",
      onSuccess: (res) => {
        setRecomputingStyles(false);
        message.success(res?.message || "Content Style recomputed.");
        if ((res?.data?.updated ?? 1) !== 0) {
          fetchReport();
        } else {
          setNeedsContentStyleRecompute(false);
        }
      },
      onError: (err) => {
        setRecomputingStyles(false);
        message.error(err?.message || "Failed to recompute Content Style.");
      },
    });
  };

  const handleRecomputeContentStyles = () => {
    Modal.confirm({
      title: "Recompute Content Style?",
      icon: <ExclamationCircleOutlined />,
      content:
        "This re-derives the Content Style (Image/Video/Poll/Carousel) for every app-published row from its actual post type. Manually added rows and any other edits are left untouched.",
      okText: "Recompute",
      cancelText: "Cancel",
      onOk: doRecomputeContentStyles,
    });
  };

  const handleImageUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => setTopPostImage(e.target.result);
    reader.readAsDataURL(file);
    return false;
  };

  // ── Engagement table editors ─────────────────────────────────────────────
  // Editable, same pattern as the blogs-status table below — seeded from
  // the live per-post computation (auto.engagementRows) until the admin
  // edits/adds a row, at which point the edited list is what persists.
  const updateEngagementRow = (idx, key, value) => {
    setEngagementRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  };
  const addEngagementRow = () =>
    setEngagementRows((prev) => [
      ...prev,
      { date: "", url: "", likes: 0, comments: 0, contentStyle: "" },
    ]);
  const removeEngagementRow = (idx) => {
    const row = engagementRows[idx];
    // A row made from a real post would just be re-added on the next load, so
    // remember the post as removed for this report.
    if (row?.postId) {
      setExcludedEngagementPostIds((prev) => (prev.includes(String(row.postId)) ? prev : [...prev, String(row.postId)]));
    }
    setEngagementRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const engagementColumns = useMemo(
    () => [
      {
        title: "Date",
        dataIndex: "date",
        width: 140,
        // Rows auto-generated from an actual app-published post (postId set)
        // get their date from the API and shouldn't be hand-edited — only
        // url + contentStyle are editable for those. Manually added rows
        // (no postId) stay fully editable.
        render: (value, row, idx) =>
          row.postId ? (
            <span style={{ color: "#374151" }}>{value || "—"}</span>
          ) : (
            <Input
              value={value}
              onChange={(e) => updateEngagementRow(idx, "date", e.target.value)}
              placeholder="DD/MM/YYYY"
            />
          ),
      },
      {
        title: <span>LinkedIn Link <span style={{ color: "#f5222d" }}>*</span></span>,
        dataIndex: "url",
        width: 200,
        render: (value, _, idx) => (
          <Input
            value={value}
            onChange={(e) => updateEngagementRow(idx, "url", e.target.value)}
            placeholder="https://www.linkedin.com/..."
          />
        ),
      },
      {
        title: "Likes",
        dataIndex: "likes",
        width: 90,
        render: (value, row, idx) =>
          row.postId ? (
            <span style={{ color: "#374151" }}>{value ?? 0}</span>
          ) : (
            <InputNumber
              value={value}
              onChange={(v) => updateEngagementRow(idx, "likes", v ?? 0)}
              min={0}
              style={{ width: "100%" }}
            />
          ),
      },
      {
        title: "Comments",
        dataIndex: "comments",
        width: 110,
        render: (value, row, idx) =>
          row.postId ? (
            <span style={{ color: "#374151" }}>{value ?? 0}</span>
          ) : (
            <InputNumber
              value={value}
              onChange={(v) => updateEngagementRow(idx, "comments", v ?? 0)}
              min={0}
              style={{ width: "100%" }}
            />
          ),
      },
      {
        title: <span>Content Style <span style={{ color: "#f5222d" }}>*</span></span>,
        dataIndex: "contentStyle",
        width: 150,
        render: (value, _, idx) => (
          <Input
            value={value}
            onChange={(e) => updateEngagementRow(idx, "contentStyle", e.target.value)}
            placeholder="Personal post"
          />
        ),
      },
      {
        title: "",
        width: 40,
        // Rows made from an app-published post can be removed from this report
        // too — the post itself is untouched, it just stops being listed here.
        render: (_, row, idx) => (
          <Button
            type="link"
            size="small"
            danger
            title={row.postId ? "Remove this post from the report" : "Remove row"}
            onClick={() => removeEngagementRow(idx)}
          >
            ×
          </Button>
        ),
      },
    ],
    [],
  );

  const updateBlogRow = (idx, key, value) => {
    setBlogsStatus((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  };
  const addBlogRow = () =>
    setBlogsStatus((prev) => [...prev, { title: "", status: "", url: "" }]);
  const removeBlogRow = (idx) =>
    setBlogsStatus((prev) => prev.filter((_, i) => i !== idx));

  // Spinner only while actively loading. If data is null but loading is done,
  // fall through to render with empty defaults so the page is usable.
  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  const auto = (data && data.auto) || {};
  const topPost = auto.topPost;
  const topPostUrl = topPostUrlOverride || topPost?.url || "";

  const Cmp = ({ value, prev }) => (
    <div>
      <span style={{ fontSize: 22, fontWeight: 700, color: "#1a3166" }}>
        {value == null ? "—" : Number(value).toLocaleString()}
      </span>
      {prev != null && (
        <span style={{ color: "#64748b", fontSize: 14, marginLeft: 6 }}>
          ({Number(prev).toLocaleString()})
        </span>
      )}
    </div>
  );

  const ContentBreakdown = ({ posts, blogs }) => (
    <div style={{ color: "#64748b", fontSize: 15, fontWeight: 500, marginTop: 10 }}>
      Posts: {Number(posts || 0).toLocaleString()} · Blogs: {Number(blogs || 0).toLocaleString()}
    </div>
  );

  // Imergey logo — SVG with white wordmark + light-blue bird for dark backgrounds
  const ImergeyLogo = () => (
    <svg width="120" height="42" viewBox="0 0 150 52" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M56.4585 20.0744H59.0318V33.9248H56.4585V20.0744Z" fill="#ffffff"/>
      <path d="M75.6343 33.9286L75.616 24.74L71.0636 32.3395H69.9155L65.3631 24.8567V33.9213H62.9102V20.071H65.0278L70.5278 29.256L75.9331 20.071H78.0471L78.069 33.9213L75.6343 33.9286Z" fill="#ffffff"/>
      <path d="M92.335 31.7705V33.9282H81.9473V20.0778H92.058V22.2356H84.5205V25.833H91.2015V27.9507H84.5205V31.7705H92.335Z" fill="#ffffff"/>
      <path d="M104.406 33.9282L101.577 29.8715C101.399 29.8897 101.22 29.8971 101.041 29.8934H97.9141V33.9282H95.3408V20.0778H101.041C104.686 20.0778 106.939 21.9404 106.939 25.0057C106.981 25.9545 106.718 26.8919 106.187 27.6795C105.656 28.467 104.886 29.063 103.99 29.3795L107.176 33.9318L104.406 33.9282ZM100.921 22.2647H97.9141V27.7867H100.921C103.177 27.7867 104.347 26.7552 104.347 25.0129C104.347 23.2707 103.177 22.2538 100.921 22.2538V22.2647Z" fill="#ffffff"/>
      <path d="M119.405 26.8427H121.84V32.3646C120.279 33.5407 118.37 34.1605 116.416 34.1251C112.123 34.1251 108.959 31.14 108.959 27.0031C108.959 22.8662 112.123 19.8774 116.456 19.8774C118.775 19.8774 120.714 20.6502 122.018 22.1336L120.396 23.7191C119.904 23.2048 119.31 22.7984 118.653 22.5255C117.995 22.2525 117.288 22.1191 116.577 22.1336C113.628 22.1336 111.55 24.131 111.55 27.0031C111.55 29.8133 113.628 31.8689 116.558 31.8689C117.554 31.8896 118.536 31.6443 119.405 31.1582V26.8427Z" fill="#ffffff"/>
      <path d="M135.752 31.7705V33.9282H125.36V20.0778H135.475V22.2356H127.934V25.833H134.622V27.9507H127.934V31.7705H135.752Z" fill="#ffffff"/>
      <path d="M144.576 29.0407V33.9284H142.003V29.0808L136.561 20.0744H139.313L143.37 26.8246L147.466 20.0744H150L144.576 29.0407Z" fill="#ffffff"/>
      <path d="M46.7705 18.6458L40.221 20.2636L38.1409 32.3599L26.5069 39.7558L22.6528 51.7771L13.6391 45.5368L20.4197 37.7537L0.387205 17.1029L0 0L29.6645 19.183L37.4476 11.7872L46.7705 18.6458Z" fill="#60a5fa"/>
      <path d="M20.6572 10.6791L29.0496 16.3341L34.3654 11.2464L34.7496 1.77045L26.7384 3.46634L20.6572 10.6791Z" fill="#93c5fd"/>
    </svg>
  );

  return (
    <div style={{ padding: "12px 0" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 18,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h3 style={{ margin: 0, color: "#1a3166" }}>
            {lang("Monthly Activity Report")}
          </h3>
          <div style={{ color: "#64748b", fontSize: 13, marginTop: 2 }}>
            {lang("Edit narrative + manual metrics, then download as PDF")}
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <MonthPicker value={selectedMonth} onChange={setSelectedMonth} />
          <Button
            icon={<SyncOutlined spin={refreshing} />}
            onClick={handleRefresh}
            loading={refreshing}
            title="Fetch fresh data from LinkedIn (bypasses cache)"
          >
            {lang("Refresh")}
          </Button>
          <Button
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
          >
            {lang("Save")}
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownloadPDF}
            loading={downloading}
          >
            {lang("Download PDF")}
          </Button>
          <Button
            icon={<MailOutlined />}
            onClick={handleOpenMailModal}
          >
            {lang("Send Mail")}
          </Button>
          <Button
            icon={<HistoryOutlined />}
            onClick={handleOpenAuditModal}
          >
            {lang("Audit Trail")}
          </Button>
          {needsContentStyleRecompute && (
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRecomputeContentStyles}
              loading={recomputingStyles}
              title="Re-derive Content Style for saved rows from each post's real type"
            >
              {lang("Recompute Content Style")}
            </Button>
          )}
        </div>
      </div>

      <Card
        style={{
          background: "#1a3166",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          marginBottom: 18,
        }}
        bodyStyle={{ padding: 24, position: "relative" }}
      >
        {/* Logo — top right */}
        <div style={{ position: "absolute", top: 20, right: 24 }}>
          <ImergeyLogo />
        </div>

        <div style={{ fontSize: 12, opacity: 0.85, letterSpacing: 0.5, fontWeight: 600 }}>
          MONTHLY ACTIVITY REPORT: {selectedMonth.format("MMMM YYYY")}
        </div>
        <div style={{ fontSize: 12, opacity: 0.85, letterSpacing: 0.5 }}>
          LEADER: {data?.leaderName || "—"}
        </div>
        <h2 style={{ color: "#fff", margin: "10px 0 14px", fontWeight: 300, letterSpacing: 0.5 }}>
          EXECUTIVE PERSONAL BRANDING
        </h2>
        {/* Lead tag */}
        <div style={{
          background: "rgba(255,255,255,0.08)",
          borderLeft: "3px solid #60a5fa",
          padding: "8px 12px",
          borderRadius: 4,
          fontSize: 12,
          marginBottom: 14,
          color: "#fff",
        }}>
          Personal Branding management and progress report for the month by IMERGEY.
        </div>
        <div
          style={{
            fontSize: 11,
            color: "#93c5fd",
            fontWeight: 700,
            letterSpacing: 1.5,
            textTransform: "uppercase",
            marginBottom: 6,
          }}
        >
          Executive Summary <span style={{ color: "#fbbf24" }}>(editable)</span> <span style={{ color: "#f5222d" }}>*</span>
        </div>
        <TextArea
          value={executiveSummary}
          onChange={(e) => setExecutiveSummary(e.target.value)}
          autoSize={{ minRows: 3, maxRows: 8 }}
          placeholder="Summarize the month's performance and key takeaways…"
          style={{
            background: "rgba(255,255,255,0.95)",
            color: "#1a3166",
            fontSize: 13,
            border: "none",
          }}
        />
      </Card>

      {/* ── Editable-fields warning ──────────────────────────────────────── */}
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 18 }}
        message="Fields marked with * are editable."
        description="Saving this report locks in whatever you've typed as a fixed override — the live LinkedIn feed will stop automatically refreshing those specific values until the override is cleared."
      />

      <h3 className="monthly-report-highlights-title" style={{ color: "#1a3166", margin: "10px 0 14px" }}>
        {lang("Account Highlights")}
      </h3>
      <Row className="monthly-report-highlights" gutter={[20, 28]} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12} md={8}>
          <Card size="small" title={<span>① Total Posts Created <Tag color="orange">editable</Tag></span>} bordered>
            <div style={{ fontSize: 12, color: "#6b7280", fontStyle: "italic", marginBottom: 6 }}>
              Content created this month
            </div>
            <Cmp value={auto.postsCreated} prev={auto.postsCreatedPrev} />
            <ContentBreakdown posts={auto.postsCreatedPostOnly} blogs={auto.postsCreatedBlogOnly} />
            <OverrideInputs
              value={postsCreatedOverride}
              onChange={setPostsCreatedOverride}
              prevValue={postsCreatedPrevOverride}
              onPrevChange={setPostsCreatedPrevOverride}
              autoValue={auto.postsCreated}
              autoPrev={auto.postsCreatedPrev}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card size="small" title={<span>② Total Content Published <Tag color="orange">editable</Tag></span>} bordered>
            <div style={{ fontSize: 12, color: "#6b7280", fontStyle: "italic", marginBottom: 6 }}>
              Posts and blogs published on account
            </div>
            <Cmp value={auto.postsPublished} prev={auto.postsPublishedPrev} />
            <ContentBreakdown posts={auto.postsPublishedPostOnly} blogs={auto.postsPublishedBlogOnly} />
            <OverrideInputs
              value={postsPublishedOverride}
              onChange={setPostsPublishedOverride}
              prevValue={postsPublishedPrevOverride}
              onPrevChange={setPostsPublishedPrevOverride}
              autoValue={auto.postsPublished}
              autoPrev={auto.postsPublishedPrev}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card size="small" title={<span>③ Followers <Tag color="orange">editable</Tag></span>} bordered>
            <div style={{ fontSize: 12, color: "#6b7280", fontStyle: "italic", marginBottom: 6 }}>
              As of month closure
            </div>
            <Cmp value={auto.followers} prev={auto.followersPrev} />
            <div style={{ marginTop: 10, fontSize: 12, fontWeight: 600 }}>
              Growth: {auto.followersGrowth >= 0 ? "+" : ""}
              {auto.followersGrowth} <span style={{ color: "#64748b" }}>(prev {auto.followersGrowthPrev >= 0 ? "+" : ""}{auto.followersGrowthPrev})</span>
            </div>
            <OverrideInputs
              value={followersOverride}
              onChange={setFollowersOverride}
              prevValue={followersPrevOverride}
              onPrevChange={setFollowersPrevOverride}
              autoValue={auto.followers}
              autoPrev={auto.followersPrev}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card size="small" title={<span>④ Impressions &amp; Engagement <Tag color="orange">editable</Tag></span>} bordered>
            <div style={{ fontSize: 12, color: "#6b7280", fontStyle: "italic", marginBottom: 6 }}>
              Impressions
            </div>
            <Cmp value={auto.contentImpressions} prev={auto.contentImpressionsPrev} />
            <OverrideInputs
              value={contentImpressionsOverride}
              onChange={setContentImpressionsOverride}
              prevValue={contentImpressionsPrevOverride}
              onPrevChange={setContentImpressionsPrevOverride}
              autoValue={auto.contentImpressions}
              autoPrev={auto.contentImpressionsPrev}
            />
            <div style={{ fontSize: 12, color: "#6b7280", fontStyle: "italic", margin: "10px 0 6px" }}>
              Engagement
            </div>
            <Cmp value={auto.contentEngagement} prev={auto.contentEngagementPrev} />
            <OverrideInputs
              value={contentEngagementOverride}
              onChange={setContentEngagementOverride}
              prevValue={contentEngagementPrevOverride}
              onPrevChange={setContentEngagementPrevOverride}
              autoValue={auto.contentEngagement}
              autoPrev={auto.contentEngagementPrev}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={16}>
          <Card
            size="small"
            title={<span>⑤ SSI Score &amp; Profile Views <Tag color="orange">editable</Tag></span>}
            bordered
          >
            <Row gutter={12}>
              <Col span={6}>
                <div style={{ fontSize: 12, color: "#6b7280" }}>SSI This Month <span style={{ color: "#f5222d" }}>*</span></div>
                <InputNumber value={ssiScore} onChange={setSsiScore} min={0} max={100} style={{ width: "100%" }} />
              </Col>
              <Col span={6}>
                <div style={{ fontSize: 12, color: "#6b7280" }}>SSI Previous <span style={{ color: "#f5222d" }}>*</span></div>
                <InputNumber value={ssiScorePrev} onChange={setSsiScorePrev} min={0} max={100} style={{ width: "100%" }} />
              </Col>
              <Col span={6}>
                <div style={{ fontSize: 12, color: "#6b7280" }}>Profile Views <span style={{ color: "#f5222d" }}>*</span></div>
                <InputNumber value={profileViews} onChange={setProfileViews} min={0} style={{ width: "100%" }} />
              </Col>
              <Col span={6}>
                <div style={{ fontSize: 12, color: "#6b7280" }}>Profile Views Prev <span style={{ color: "#f5222d" }}>*</span></div>
                <InputNumber value={profileViewsPrev} onChange={setProfileViewsPrev} min={0} style={{ width: "100%" }} />
              </Col>
            </Row>
            <div style={{ marginTop: 8, fontSize: 11, color: "#6b7280" }}>
              LinkedIn API does not expose these — copy them in manually each month.
            </div>
          </Card>
        </Col>
      </Row>

      <Row className="monthly-report-post-section" gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={14}>
          <Card
            size="small"
            title={<span>📊 Engagement Tracking of Posts <Tag color="orange">editable</Tag></span>}
            bordered
          >
            <Table
              size="small"
              rowKey={(_, i) => i}
              dataSource={engagementRows}
              columns={engagementColumns}
              pagination={false}
              scroll={{ y: 500 }}
              locale={{ emptyText: "No rows yet — click '+ Add row' below to add one." }}
            />
            <Button
              type="link"
              icon={<PlusOutlined />}
              onClick={addEngagementRow}
              style={{ paddingLeft: 4, marginTop: 4 }}
            >
              Add row
            </Button>
          </Card>
          <Card
            size="small"
            title={<span>📁 Status of Blogs <Tag color="orange">editable</Tag></span>}
            bordered
            style={{ marginTop: 14 }}
          >
            <Table
              size="small"
              tableLayout="fixed"
              rowKey={(_, i) => i}
              dataSource={blogsStatus}
              pagination={false}
              columns={[
                {
                  title: <span>Blog Title <span style={{ color: "#f5222d" }}>*</span></span>,
                  dataIndex: "title",
                  width: "40%",
                  render: (val, _, idx) => (
                    <Input
                      value={val}
                      onChange={(e) => updateBlogRow(idx, "title", e.target.value)}
                      placeholder="Blog title"
                    />
                  ),
                },
                {
                  title: <span>Status <span style={{ color: "#f5222d" }}>*</span></span>,
                  dataIndex: "status",
                  width: 160,
                  render: (val, _, idx) => (
                    <Input
                      value={formatBlogStatusDisplay(val)}
                      onChange={(e) => updateBlogRow(idx, "status", e.target.value)}
                      placeholder="Draft / Published / TBD"
                    />
                  ),
                },
                {
                  title: "LinkedIn Link",
                  dataIndex: "url",
                  render: (val, _, idx) => (
                    <Input
                      value={val}
                      onChange={(e) => updateBlogRow(idx, "url", e.target.value)}
                      placeholder="https://www.linkedin.com/..."
                    />
                  ),
                },
                {
                  title: "",
                  width: 40,
                  render: (_, __, idx) => (
                    <Button
                      type="link"
                      size="small"
                      danger
                      onClick={() => removeBlogRow(idx)}
                    >
                      ×
                    </Button>
                  ),
                },
              ]}
              locale={{ emptyText: "No blogs added — click '+ Add row' below to add one." }}
            />
            <Button
              type="link"
              icon={<PlusOutlined />}
              onClick={addBlogRow}
              style={{ paddingLeft: 4, marginTop: 4 }}
            >
              Add row
            </Button>
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card
            size="small"
            title={<span>🏆 Top Performing Post <Tag color="orange">editable</Tag></span>}
            bordered
            style={{ marginBottom: 14 }}
          >
            {!topPost && (
              <div style={{ color: "#6b7280", fontStyle: "italic", fontSize: 12, marginBottom: 12 }}>
                No published posts or engagement rows this month yet — enter the top post by hand below.
              </div>
            )}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>
                Title:
              </div>
              <Input
                value={topPostTitleOverride}
                onChange={(e) => setTopPostTitleOverride(e.target.value)}
                placeholder={topPost?.title || "e.g. post title / content style"}
              />
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Likes:</div>
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  value={topPostLikesOverride}
                  onChange={setTopPostLikesOverride}
                  placeholder={String(topPost?.likes ?? 0)}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Comments:</div>
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  value={topPostCommentsOverride}
                  onChange={setTopPostCommentsOverride}
                  placeholder={String(topPost?.comments ?? 0)}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Shares:</div>
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  value={topPostSharesOverride}
                  onChange={setTopPostSharesOverride}
                  placeholder={String(topPost?.shares ?? 0)}
                />
              </div>
            </div>
            {topPost && (
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8 }}>
                👁 {topPost.impressions.toLocaleString()} impressions
              </div>
            )}
            {topPostUrl && (
              <a
                href={topPostUrl}
                target="_blank"
                rel="noreferrer"
                style={{ fontSize: 11, wordBreak: "break-all" }}
              >
                {topPostUrl}
              </a>
            )}
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>
                {topPost ? "Override URL (optional):" : "LinkedIn URL:"}
              </div>
              <Input
                value={topPostUrlOverride}
                onChange={(e) => setTopPostUrlOverride(e.target.value)}
                placeholder={topPost?.url || "https://www.linkedin.com/..."}
              />
            </div>
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>
                Upload screenshot (for PDF):
              </div>
              <Upload
                beforeUpload={handleImageUpload}
                showUploadList={false}
                accept="image/*"
              >
                <Button icon={<UploadOutlined />} size="small">
                  {topPostImage ? "Replace image" : "Upload image"}
                </Button>
              </Upload>
              {topPostImage && (
                <div style={{ marginTop: 8 }}>
                  <img
                    src={topPostImage}
                    alt="Top post"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 280,
                      border: "1px solid #e5e7eb",
                    }}
                  />
                  <Button
                    type="link"
                    size="small"
                    danger
                    onClick={() => setTopPostImage("")}
                  >
                    Remove
                  </Button>
                </div>
              )}
            </div>
          </Card>

          <Card
            size="small"
            title={<span>🎯 Themes Focused This Month <Tag color="orange">editable</Tag></span>}
            bordered
          >
            {themes.length === 0 ? (
              <div style={{ color: "#6b7280", fontStyle: "italic", fontSize: 12 }}>
                No content themes configured for this client yet.
              </div>
            ) : (
              <div>
                {themes.map((theme) => {
                  const subPillars = (theme.subPillars || []).flat().filter(Boolean);
                  const themeId = String(theme._id);
                  const selected = selectedThemes[themeId] || [];
                  const allChecked = subPillars.length > 0 && subPillars.every((s) => selected.includes(s));
                  const someChecked = selected.length > 0 && !allChecked;

                  return (
                    <div key={themeId} style={{ marginBottom: 10 }}>
                      {/* Theme-level checkbox */}
                      <Checkbox
                        checked={allChecked}
                        indeterminate={someChecked}
                        onChange={() => handleToggleTheme(themeId, subPillars)}
                      >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
                          {theme.color && (
                            <span
                              style={{
                                width: 10, height: 10, borderRadius: "50%",
                                background: theme.color, display: "inline-block",
                                flexShrink: 0,
                              }}
                            />
                          )}
                          {theme.name}
                        </span>
                      </Checkbox>
                      {/* Sub-pillar checkboxes */}
                      {subPillars.length > 0 && (
                        <div style={{ marginLeft: 24, marginTop: 4, display: "flex", flexWrap: "wrap", gap: "4px 16px" }}>
                          {subPillars.map((sub) => (
                            <Checkbox
                              key={sub}
                              checked={selected.includes(sub)}
                              onChange={() => handleToggleSubpillar(themeId, sub)}
                              style={{ fontSize: 12, color: "#374151" }}
                            >
                              {sub}
                            </Checkbox>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                {/* Preview of what will appear in the PDF */}
                {Object.values(selectedThemes).some((v) => v.length > 0) && (
                  <div style={{
                    marginTop: 10, padding: "6px 10px",
                    background: "#f0f9ff", border: "1px solid #bae6fd",
                    borderRadius: 4, fontSize: 11, color: "#0369a1",
                  }}>
                    <strong>PDF preview:</strong>{" "}
                    {themes
                      .filter((t) => (selectedThemes[String(t._id)] || []).length > 0)
                      .map((t) => `${t.name}: ${(selectedThemes[String(t._id)] || []).join(", ")}`)
                      .join(" · ")}
                  </div>
                )}
              </div>
            )}
          </Card>
          <Card
            size="small"
            title={<span>🎯 Goals for Next Month <Tag color="orange">editable</Tag></span>}
            bordered
            style={{ marginTop: 14 }}
          >
            <TextArea
              value={goalsNextMonth}
              onChange={(e) => setGoalsNextMonth(e.target.value)}
              autoSize={{ minRows: 5, maxRows: 10 }}
              placeholder="What should the leader focus on next month? Connection building, posting cadence, themes..."
            />
          </Card>
        </Col>
      </Row>

      {/* ── Send Mail modal ───────────────────────────────────────────── */}
      <Modal
        title={
          <span>
            <MailOutlined style={{ marginRight: 8, color: "#1a3166" }} />
            {lang("Send Monthly Report by Email")}
          </span>
        }
        open={mailModalOpen}
        onCancel={() => !sendingMail && setMailModalOpen(false)}
        onOk={handleSendMail}
        okText={lang("Send")}
        confirmLoading={sendingMail}
        width={640}
      >
        <div style={{ marginBottom: 12, fontSize: 12, color: "#64748b" }}>
          The current {selectedMonth.format("MMMM YYYY")} report PDF will be attached automatically.
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
            To <span style={{ color: "#f5222d" }}>*</span>
          </div>
          <Select
            mode="tags"
            notFoundContent={null}
            tokenSeparators={[",", ";", " "]}
            value={mailTo}
            onChange={setMailTo}
            placeholder="Type an email and press Enter"
            style={{ width: "100%" }}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Cc</div>
          <Select
            mode="tags"
            notFoundContent={null}
            tokenSeparators={[",", ";", " "]}
            value={mailCc}
            onChange={setMailCc}
            placeholder="Optional — type an email and press Enter"
            style={{ width: "100%" }}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Subject</div>
          <Input
            value={mailSubject}
            onChange={(e) => setMailSubject(e.target.value)}
            placeholder="Email subject"
          />
        </div>
        <div>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Message</div>
          <TextArea
            value={mailBody}
            onChange={(e) => setMailBody(e.target.value)}
            autoSize={{ minRows: 8, maxRows: 16 }}
            placeholder="Write the email body…"
          />
        </div>
      </Modal>

      {/* ── Audit trail modal ─────────────────────────────────────────── */}
      <Modal
        title={
          <span>
            <HistoryOutlined style={{ marginRight: 8, color: "#1a3166" }} />
            {lang("Audit Trail")} <span style={{ color: "#9ca3af", fontWeight: 400 }}>— {selectedMonth.format("MMMM YYYY")}</span>
          </span>
        }
        open={auditModalOpen}
        onCancel={() => setAuditModalOpen(false)}
        footer={null}
        width={620}
        bodyStyle={{ background: "#fafbfc", borderRadius: 8, padding: "20px 20px 4px" }}
      >
        {auditLoading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin />
          </div>
        ) : auditLogs.length === 0 ? (
          <Empty
            description="No changes or emails recorded for this report yet."
            style={{ padding: 40 }}
          />
        ) : (
          // overflowY:auto forces overflow-x to compute as auto too (CSS
          // spec), which was clipping the custom Timeline dots — they sit
          // a few px to the left of this container's own edge. paddingLeft
          // gives them room instead of getting cut off.
          <div style={{ maxHeight: 520, overflowY: "auto", paddingRight: 4, paddingLeft: 16 }}>
            <Timeline>
              {auditLogs.map((log, logIdx) => {
                const isEmail = log.action === "email_sent";
                return (
                  <Timeline.Item
                    key={log._id || logIdx}
                    color={isEmail ? "green" : "blue"}
                    dot={
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          fontSize: 12,
                          lineHeight: 1,
                          background: isEmail ? "#e6f9ec" : "#eaf1ff",
                          color: isEmail ? "#389e0d" : "#1a3166",
                          border: `1px solid ${isEmail ? "#b7eb8f" : "#adc6ff"}`,
                        }}
                      >
                        {isEmail ? <MailOutlined /> : <EditOutlined />}
                      </span>
                    }
                  >
                    <Card
                      size="small"
                      bordered
                      style={{ marginBottom: 14, borderRadius: 8, boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}
                      bodyStyle={{ padding: "10px 14px" }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6, flexWrap: "wrap", gap: 6 }}>
                        <div>
                          <strong style={{ fontSize: 13.5 }}>{log.actorName}</strong>{" "}
                          <Tag color={isEmail ? "green" : "blue"} style={{ marginLeft: 2 }}>
                            {log.actorRole}
                          </Tag>
                        </div>
                        <span style={{ fontSize: 11.5, color: "#9ca3af", whiteSpace: "nowrap" }}>
                          {dayjs(log.at).format("DD MMM YYYY, hh:mm A")}
                        </span>
                      </div>

                      {isEmail ? (
                        <div style={{ fontSize: 13 }}>
                          Sent the report to <strong>{log.recipientEmail}</strong>
                          {log.subject ? (
                            <div style={{ marginTop: 4, color: "#6b7280", fontStyle: "italic" }}>
                              "{log.subject}"
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {(log.changes || []).map((c, i) => {
                            const kind = getChangeKind(c.label);
                            const style = CHANGE_KIND_STYLE[kind];
                            const hasValues = c.oldValue !== null || c.newValue !== null;
                            return (
                              <div
                                key={i}
                                style={{
                                  fontSize: 12.5,
                                  background: style.bg,
                                  border: `1px solid ${style.border}`,
                                  borderRadius: 6,
                                  padding: "6px 10px",
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 8,
                                }}
                              >
                                <span style={{ color: style.color, marginTop: 2 }}>
                                  {kind === "add" ? <PlusOutlined /> : kind === "remove" ? <MinusOutlined /> : <EditOutlined />}
                                </span>
                                <div>
                                  <div style={{ fontWeight: 600, color: style.color }}>{c.label}</div>
                                  {hasValues && (
                                    <div style={{ marginTop: 2 }}>
                                      <span style={{ color: "#9ca3af", textDecoration: "line-through" }}>
                                        {c.oldValue === null || c.oldValue === "" ? "—" : String(c.oldValue)}
                                      </span>
                                      <span style={{ margin: "0 6px", color: "#9ca3af" }}>→</span>
                                      <span style={{ color: "#1a3166", fontWeight: 600 }}>
                                        {c.newValue === null || c.newValue === "" ? "—" : String(c.newValue)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </Card>
                  </Timeline.Item>
                );
              })}
            </Timeline>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MonthlyReportTab;
